import { useMemo, useState } from 'react';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  ProjectFilter,
  RoleFilter,
  StagedEntry,
  applySelectionToIds,
  bulkUpdateStagedValues,
  extractContactIdsByNewValue,
  filterActualChanges,
  filterContacts,
  filterEntriesByNewValue,
  indexContactsById,
  mergeSelectedIntoStaging,
  removeStagedEntry,
  toggleIdInSelection,
  updateStagedEntryValue,
  getOriginalAllowEmailChange,
} from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/helpers';

export type StepId = 1 | 2 | 3;

export const WIZARD_STEP_PICK: StepId = 1;
export const WIZARD_STEP_REVIEW: StepId = 2;
export const WIZARD_STEP_CONFIRMATION: StepId = 3;

export interface UseEmailSelfServiceFormStateArgs {
  contacts: IContact[];
}

export interface UseEmailSelfServiceFormStateReturn {
  currentStep: StepId;
  setCurrentStep: (step: StepId) => void;

  roleFilter: RoleFilter;
  projectFilter: ProjectFilter;
  searchQuery: string;
  setRoleFilter: (role: RoleFilter) => void;
  setProjectFilter: (projectId: ProjectFilter) => void;
  setSearchQuery: (query: string) => void;

  selectedIds: Set<string>;
  toggleSelection: (id: string) => void;
  setSelection: (ids: string[], selected: boolean) => void;
  clearSelection: () => void;

  setStagedValue: (id: string, allow: boolean) => void;
  removeFromStaging: (id: string) => void;
  bulkSetStaged: (allow: boolean) => void;
  clearStaging: () => void;

  filteredContacts: IContact[];
  stagedEntries: StagedEntry[];
  actualChanges: StagedEntry[];
  unchangedContacts: StagedEntry[];
  nowAllowedEntries: StagedEntry[];
  nowDisallowedEntries: StagedEntry[];
  willAllowIds: string[];
  willDisallowIds: string[];

  canContinueFromStep1: boolean;
  canApplyFromStep2: boolean;

  stageSelectedAndContinue: () => void;
  goToStep: (step: StepId) => void;

  reset: () => void;
}

export type {
  ProjectFilter,
  RoleFilter,
  StagedEntry,
} from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/helpers';

export const useEmailSelfServiceFormState = ({
  contacts,
}: UseEmailSelfServiceFormStateArgs): UseEmailSelfServiceFormStateReturn => {
  const [currentStep, setCurrentStepState] = useState<StepId>(WIZARD_STEP_PICK);
  const [roleFilter, setRoleFilterState] = useState<RoleFilter>('all');
  const [projectFilter, setProjectFilterState] = useState<ProjectFilter>('all');
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [selectedIds, setSelectedIdsState] = useState<Set<string>>(new Set());
  const [staged, setStagedState] = useState<Map<string, StagedEntry>>(
    new Map()
  );

  const setCurrentStep = (step: StepId): void => {
    setCurrentStepState(step);
  };

  const setRoleFilter = (role: RoleFilter): void => {
    setRoleFilterState(role);
  };

  const setProjectFilter = (projectId: ProjectFilter): void => {
    setProjectFilterState(projectId);
  };

  const setSearchQuery = (query: string): void => {
    setSearchQueryState(query);
  };

  const toggleSelection = (id: string): void => {
    setSelectedIdsState((previous: Set<string>) =>
      toggleIdInSelection(previous, id)
    );
  };

  const setSelection = (ids: string[], selected: boolean): void => {
    setSelectedIdsState((previous: Set<string>) =>
      applySelectionToIds(previous, ids, selected)
    );
  };

  const clearSelection = (): void => {
    setSelectedIdsState(new Set());
  };

  const setStagedValue = (id: string, allow: boolean): void => {
    setStagedState((previous: Map<string, StagedEntry>) =>
      updateStagedEntryValue(previous, id, allow)
    );
  };

  const removeFromStaging = (id: string): void => {
    setStagedState((previous: Map<string, StagedEntry>) =>
      removeStagedEntry(previous, id)
    );
  };

  const bulkSetStaged = (allow: boolean): void => {
    setStagedState((previous: Map<string, StagedEntry>) =>
      bulkUpdateStagedValues(previous, allow)
    );
  };

  const clearStaging = (): void => {
    setStagedState(new Map());
  };

  const stageSelectedAndContinue = (): void => {
    if (!canContinueFromStep1) {
      return;
    }

    setStagedState((previous: Map<string, StagedEntry>) =>
      mergeSelectedIntoStaging(previous, selectedIds, contactsById)
    );

    setCurrentStepState(WIZARD_STEP_REVIEW);
  };

  const goToStep = (step: StepId): void => {
    if (step === WIZARD_STEP_REVIEW && !canContinueFromStep1) {
      return;
    }

    if (step === WIZARD_STEP_CONFIRMATION && !canApplyFromStep2) {
      return;
    }

    if (step === WIZARD_STEP_PICK) {
      setStagedState(new Map());
    }

    setCurrentStepState(step);
  };

  const reset = (): void => {
    setCurrentStepState(WIZARD_STEP_PICK);
    setRoleFilterState('all');
    setProjectFilterState('all');
    setSearchQueryState('');
    setSelectedIdsState(new Set());
    setStagedState(new Map());
  };

  const contactsById: Map<string, IContact> = useMemo(() => {
    return indexContactsById(contacts);
  }, [contacts]);

  const filteredContacts: IContact[] = useMemo(() => {
    return filterContacts(contacts, roleFilter, projectFilter, searchQuery);
  }, [contacts, roleFilter, projectFilter, searchQuery]);

  const stagedEntries: StagedEntry[] = useMemo(() => {
    return Array.from(staged.values());
  }, [staged]);

  const actualChanges: StagedEntry[] = useMemo(() => {
    return filterActualChanges(stagedEntries);
  }, [stagedEntries]);

  const unchangedContacts: StagedEntry[] = useMemo(() => {
    return stagedEntries.filter(
      (entry: StagedEntry) =>
        getOriginalAllowEmailChange(entry.contact) === entry.newValue
    );
  }, [stagedEntries]);

  const nowAllowedEntries: StagedEntry[] = useMemo(() => {
    return filterEntriesByNewValue(actualChanges, true);
  }, [actualChanges]);

  const nowDisallowedEntries: StagedEntry[] = useMemo(() => {
    return filterEntriesByNewValue(actualChanges, false);
  }, [actualChanges]);

  const willAllowIds: string[] = useMemo(() => {
    return extractContactIdsByNewValue(actualChanges, true);
  }, [actualChanges]);

  const willDisallowIds: string[] = useMemo(() => {
    return extractContactIdsByNewValue(actualChanges, false);
  }, [actualChanges]);

  const canContinueFromStep1: boolean = selectedIds.size > 0;
  const canApplyFromStep2: boolean = actualChanges.length > 0;

  return {
    currentStep,
    setCurrentStep,

    roleFilter,
    projectFilter,
    searchQuery,
    setRoleFilter,
    setProjectFilter,
    setSearchQuery,

    selectedIds,
    toggleSelection,
    setSelection,
    clearSelection,

    setStagedValue,
    removeFromStaging,
    bulkSetStaged,
    clearStaging,

    filteredContacts,
    stagedEntries,
    actualChanges,
    unchangedContacts,
    nowAllowedEntries,
    nowDisallowedEntries,
    willAllowIds,
    willDisallowIds,

    canContinueFromStep1,
    canApplyFromStep2,

    stageSelectedAndContinue,
    goToStep,

    reset,
  };
};
